import React, { useState, useEffect, useMemo, useContext } from 'react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { GlobalContext } from '../../../context/GlobalContext';
import useApi from '../../../hooks/useApi';
import haxios from '../../../lib/haxios';
import ReportLayout from '../core/ReportLayout';
import ReportKPICards from '../core/ReportKPICards';
import DataTable from '../shared/DataTable';
import CustomerProfileModal from '../../../components/CustomerProfileModal';
import { Plus, X, Send, Loader2, MessageSquare, Search, Filter } from 'lucide-react';

const CustomerContactsReport = () => {
  const { uinfo, apir } = useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [filters, setFilters] = useState({
    staff: 'all',
    dateRange: 'all',
    accountSearch: ''
  });
  const [showAddMessageModal, setShowAddMessageModal] = useState(false);
  const [newMessage, setNewMessage] = useState({ account: '', message: '' });
  const [isSending, setIsSending] = useState(false);
  
  // Customer Profile Modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [customerMessages, setCustomerMessages] = useState([]);
  const [profileNewMessage, setProfileNewMessage] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [formData, setFormData] = useState({
    numberOfRamps: 0,
    numberOfTechnicians: 0,
    providesMOT: false,
    providesOilService: false,
    providesEVService: false,
    openingHours: {
      monday: { open: '', close: '' },
      tuesday: { open: '', close: '' },
      wednesday: { open: '', close: '' },
      thursday: { open: '', close: '' },
      friday: { open: '', close: '' },
      saturday: { open: '', close: '' },
      sunday: { open: '', close: '' }
    },
    initialComment: '',
    rebateStructure: {
      isGlobalPercent: true,
      tiers: []
    }
  });

  // Get operators for filtering
  const { data: salesOperators, isLoading: operatorsLoading } = useApi({ 
    url: '/salesassist/cplusassist/getOperators', 
    initial: [] 
  });

  // Get customers for autocomplete/search
  const { data: customersData, isLoading: customersLoading } = useApi({
    url: '/visiondashboard/customers/profiles',
    initial: [],
    enabled: !!apir
  });

  // Fetch messages (same API as dashboard MessageList)
  const fetchMessages = async () => {
    try {
      setLoading(true);
      console.log('CustomerContactsReport - Fetching messages');
      
      const params = {};
      if (uinfo?.Status !== 'admin') {
        params.acct = uinfo?.Acct;
      }

      const response = await haxios.get('/visiondashboard/messages', { params });
      const messagesData = response.data || [];
      
      console.log('CustomerContactsReport - Messages count:', messagesData.length);
      setMessages(messagesData);
    } catch (error) {
      console.error('CustomerContactsReport - Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch customer profile and messages
  const fetchCustomerProfile = async (accountId) => {
    try {
      setLoadingProfile(true);
      
      // Fetch profile
      const profileResponse = await haxios.get(`/visiondashboard/customer/profile/${accountId}`);
      const profile = profileResponse.data || profileResponse;
      
      // Fetch messages for this customer
      const messagesResponse = await haxios.get(`/visiondashboard/messages?acct=${accountId}`);
      const customerMsgs = Array.isArray(messagesResponse.data) ? messagesResponse.data : messagesResponse.data?.data || [];
      
      // Find customer from customers list
      const customer = customersData?.find(c => c.KeyCode === accountId || c.Acct === accountId);
      
      setCustomerProfile(profile);
      setCustomerMessages(customerMsgs);
      setSelectedCustomer(customer || { KeyCode: accountId, Name: accountId });
      
      // Set form data from profile
      if (profile) {
        setFormData({
          numberOfRamps: profile.numberOfRamps || 0,
          numberOfTechnicians: profile.numberOfTechnicians || 0,
          providesMOT: profile.providesMOT || false,
          providesOilService: profile.providesOilService || false,
          providesEVService: profile.providesEVService || false,
          openingHours: profile.openingHours || {
            monday: { open: '', close: '' },
            tuesday: { open: '', close: '' },
            wednesday: { open: '', close: '' },
            thursday: { open: '', close: '' },
            friday: { open: '', close: '' },
            saturday: { open: '', close: '' },
            sunday: { open: '', close: '' }
          },
          initialComment: '',
          rebateStructure: profile.rebateStructure || {
            isGlobalPercent: true,
            tiers: []
          }
        });
      }
    } catch (error) {
      console.error('Error fetching customer profile:', error);
      // Still show modal with basic info
      const customer = customersData?.find(c => c.KeyCode === accountId || c.Acct === accountId);
      setSelectedCustomer(customer || { KeyCode: accountId, Name: accountId });
      setCustomerMessages([]);
    } finally {
      setLoadingProfile(false);
    }
  };

  // Handle row click to open profile modal
  const handleRowClick = (row) => {
    if (row.account && row.account !== 'N/A') {
      fetchCustomerProfile(row.account);
      setShowProfileModal(true);
    }
  };

  // Handle send message from profile modal
  const handleProfileSendMessage = async () => {
    if (!profileNewMessage.trim() || !selectedCustomer) return;
    
    try {
      const response = await haxios.post(
        `/visiondashboard/customer/profile/${selectedCustomer.KeyCode}/messages`,
        {
          staffMember: uinfo?.Name || 'Unknown',
          message: profileNewMessage.trim(),
          op: uinfo?.Inits || 'Unknown'
        }
      );

      if (Array.isArray(response)) {
        setCustomerMessages(response);
      } else if (response.data) {
        setCustomerMessages(Array.isArray(response.data) ? response.data : [response.data]);
      }
      
      setProfileNewMessage('');
      await fetchMessages(); // Refresh main messages list
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message.');
    }
  };

  // Handle save profile
  const handleSaveProfile = async () => {
    if (!selectedCustomer) return;
    
    try {
      const profileData = {
        keyCode: selectedCustomer.KeyCode,
        numberOfRamps: formData.numberOfRamps,
        numberOfTechnicians: formData.numberOfTechnicians,
        providesMOT: formData.providesMOT,
        providesOilService: formData.providesOilService,
        providesEVService: formData.providesEVService,
        openingHours: formData.openingHours,
        rebateStructure: formData.rebateStructure
      };

      await haxios.put(`/visiondashboard/customer/profile/${selectedCustomer.KeyCode}`, profileData);
      await fetchMessages(); // Refresh messages
      alert('Profile saved successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile.');
    }
  };

  // Fetch data when component mounts
  useEffect(() => {
    fetchMessages();
  }, [uinfo?.Status, uinfo?.Acct]);

  // Apply filters to messages
  const filteredMessages = useMemo(() => {
    let filtered = [...messages];

    // Filter by staff member
    if (filters.staff && filters.staff !== 'all') {
      filtered = filtered.filter(msg => {
        const staff = msg.staff_member || msg.Op;
        return staff === filters.staff;
      });
    }

    // Filter by date range
    if (filters.dateRange && filters.dateRange !== 'all') {
      const now = new Date();
      let startDate;
      
      switch (filters.dateRange) {
        case 'today':
          startDate = startOfDay(now);
          break;
        case 'week':
          startDate = startOfDay(subDays(now, 7));
          break;
        case 'month':
          startDate = startOfDay(subDays(now, 30));
          break;
        default:
          startDate = null;
      }
      
      if (startDate) {
        filtered = filtered.filter(msg => {
          try {
            const msgDate = new Date(msg.timestamp);
            return msgDate >= startDate;
          } catch {
            return false;
          }
        });
      }
    }

    // Filter by account search
    if (filters.accountSearch) {
      const searchTerm = filters.accountSearch.toLowerCase();
      filtered = filtered.filter(msg => {
        const account = (msg.Acct || '').toLowerCase();
        return account.includes(searchTerm);
      });
    }

    return filtered;
  }, [messages, filters]);

  // Process data for KPI cards and table
  const processedData = useMemo(() => {
    if (!filteredMessages || filteredMessages.length === 0) {
      return {
        kpis: [],
        tableData: []
      };
    }

    // Calculate metrics from filtered messages
    const totalMessages = filteredMessages.length;
    
    // Count unique customers (by Acct)
    const uniqueCustomers = new Set();
    filteredMessages.forEach(msg => {
      if (msg.Acct) {
        uniqueCustomers.add(msg.Acct);
      }
    });
    const uniqueCustomersCount = uniqueCustomers.size;
    
    // Count unique staff members
    const uniqueStaff = new Set();
    filteredMessages.forEach(msg => {
      const staff = msg.staff_member || msg.Op;
      if (staff) {
        uniqueStaff.add(staff);
      }
    });
    const uniqueStaffCount = uniqueStaff.size;
    
    // Calculate average messages per customer
    const avgMessagesPerCustomer = uniqueCustomersCount > 0 ? totalMessages / uniqueCustomersCount : 0;
    
    // Get messages from last 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recentMessages = filteredMessages.filter(msg => {
      try {
        const msgDate = new Date(msg.timestamp);
        return msgDate >= sevenDaysAgo;
      } catch {
        return false;
      }
    }).length;

    // Create KPI cards
    const kpis = [
      {
        key: 'total-messages',
        label: 'Total Messages',
        value: totalMessages,
        type: 'bdms',
        format: 'number'
      },
      {
        key: 'unique-customers',
        label: 'Unique Customers',
        value: uniqueCustomersCount,
        type: 'customers',
        format: 'number'
      },
      {
        key: 'avg-messages-per-customer',
        label: 'Avg Messages per Customer',
        value: avgMessagesPerCustomer,
        type: 'bdms',
        format: 'decimal'
      },
      {
        key: 'recent-messages',
        label: 'Messages (Last 7 Days)',
        value: recentMessages,
        type: 'bdms',
        format: 'number'
      }
    ];

    // Create table data
    const tableData = filteredMessages.map(msg => {
      // Find customer name from customersData
      const customer = customersData?.find(c => 
        c.KeyCode === msg.Acct || 
        c.Acct === msg.Acct ||
        c.account === msg.Acct
      );
      const accountName = customer?.Name || customer?.name || customer?.CustomerName || 'N/A';
      
      return {
        account: msg.Acct || 'N/A',
        accountName: accountName,
        staffMember: msg.staff_member || msg.Op || 'N/A',
        timestamp: msg.timestamp ? new Date(msg.timestamp) : null,
        message: msg.message || '',
        id: msg.id || Math.random()
      };
    });

    return { kpis, tableData };
  }, [filteredMessages, customersData]);

  // Table columns
  const columns = [
    {
      key: 'account',
      label: 'Account',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer">
          {value}
        </span>
      )
    },
    {
      key: 'accountName',
      label: 'Account Name',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">
          {value}
        </span>
      )
    },
    {
      key: 'staffMember',
      label: 'Staff Member',
      sortable: true
    },
    {
      key: 'timestamp',
      label: 'Date',
      sortable: true,
      render: (value) => {
        if (!value) return 'N/A';
        try {
          return format(new Date(value), 'dd/MM/yy HH:mm');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      key: 'message',
      label: 'Message',
      sortable: false,
      render: (value) => (
        <div className="truncate max-w-xl" title={value}>
          {value || 'N/A'}
        </div>
      )
    }
  ];

  // Filter options
  const filterOptions = [
    {
      key: 'staff',
      label: 'Staff Member',
      type: 'select',
      options: [
        { value: 'all', label: 'All Staff' },
        ...(salesOperators?.map(op => ({
          value: op.Inits || op.Name || op.name,
          label: `${op.Inits || op.Name || op.name} - ${op.Name || op.name || op.Inits}`
        })) || [])
      ]
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'select',
      options: [
        { value: 'all', label: 'All Time' },
        { value: 'today', label: 'Today' },
        { value: 'week', label: 'Last 7 Days' },
        { value: 'month', label: 'Last 30 Days' }
      ]
    }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearAll = () => {
    setFilters({
      staff: 'all',
      dateRange: 'all',
      accountSearch: ''
    });
  };

  const handleExportPDF = () => {
    console.log('CustomerContactsReport - Exporting to PDF');
    // TODO: Implement PDF export
  };

  const handleExportExcel = () => {
    console.log('CustomerContactsReport - Exporting to Excel');
    // TODO: Implement Excel export
  };

  const handleEmailReport = () => {
    console.log('CustomerContactsReport - Emailing report');
    // TODO: Implement email functionality
  };

  const handleAddMessage = async () => {
    if (!newMessage.account || !newMessage.message.trim()) {
      alert('Please enter both account and message');
      return;
    }

    try {
      setIsSending(true);
      await haxios.post('/visiondashboard/messages/add', {
        message: newMessage.message,
        acct: newMessage.account,
        op: uinfo?.Inits || uinfo?.Name
      });
      
      setNewMessage({ account: '', message: '' });
      setShowAddMessageModal(false);
      await fetchMessages(); // Refresh the messages list
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // Get unique accounts for autocomplete
  const uniqueAccounts = useMemo(() => {
    const accounts = new Set();
    messages.forEach(msg => {
      if (msg.Acct) {
        accounts.add(msg.Acct);
      }
    });
    return Array.from(accounts).sort();
  }, [messages]);

  return (
    <ReportLayout
      title="Customer Contacts Report"
      subtitle="Customer comments and messages tracking with staff interactions"
      icon={MessageSquare}
      iconColor="indigo"
      onExportPDF={handleExportPDF}
      onExportExcel={handleExportExcel}
      onEmailReport={handleEmailReport}
      loading={loading || operatorsLoading}
    >
      <div className="space-y-8">
        {/* KPI Cards */}
        <ReportKPICards kpis={processedData.kpis} loading={loading} />

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-gray-600">
              <Filter className="w-5 h-5" />
              <span className="font-medium">Filters:</span>
            </div>
            
            {/* Staff Filter */}
            <select
              value={filters.staff}
              onChange={(e) => handleFilterChange('staff', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {filterOptions[0].options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            {/* Date Range Filter */}
            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {filterOptions[1].options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            {/* Account Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={filters.accountSearch}
                onChange={(e) => handleFilterChange('accountSearch', e.target.value)}
                placeholder="Search by account..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {(filters.staff !== 'all' || filters.dateRange !== 'all' || filters.accountSearch) && (
              <button
                onClick={handleClearAll}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Messages Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-gray-900">Customer Contacts & Messages</h3>
              <span className="text-sm text-gray-500">
                ({processedData.tableData.length} {processedData.tableData.length === 1 ? 'message' : 'messages'})
              </span>
            </div>
            <button
              onClick={() => setShowAddMessageModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Message</span>
            </button>
          </div>
          <DataTable
            data={processedData.tableData}
            columns={columns}
            loading={loading || operatorsLoading}
            searchable={true}
            sortable={true}
            pagination={true}
            pageSize={20}
            emptyMessage="No customer contacts found"
            searchPlaceholder="Search messages..."
            onRowClick={handleRowClick}
          />
        </div>
      </div>

      {/* Add Message Modal */}
      {showAddMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Add Customer Message</h2>
              <button
                onClick={() => {
                  setShowAddMessageModal(false);
                  setNewMessage({ account: '', message: '' });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number *
                </label>
                <input
                  type="text"
                  list="account-list"
                  value={newMessage.account}
                  onChange={(e) => setNewMessage({ ...newMessage, account: e.target.value })}
                  placeholder="Enter account number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isSending}
                />
                <datalist id="account-list">
                  {uniqueAccounts.map(account => (
                    <option key={account} value={account} />
                  ))}
                </datalist>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  value={newMessage.message}
                  onChange={(e) => setNewMessage({ ...newMessage, message: e.target.value })}
                  placeholder="Enter your message..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isSending}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowAddMessageModal(false);
                    setNewMessage({ account: '', message: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={isSending}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMessage}
                  disabled={!newMessage.account || !newMessage.message.trim() || isSending}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Profile Modal */}
      {showProfileModal && selectedCustomer && (
        <CustomerProfileModal
          selectedCustomer={selectedCustomer}
          formData={formData}
          setFormData={setFormData}
          messages={customerMessages}
          newMessage={profileNewMessage}
          setNewMessage={setProfileNewMessage}
          handleSendMessage={handleProfileSendMessage}
          handleSaveProfile={handleSaveProfile}
          onClose={() => {
            setShowProfileModal(false);
            setSelectedCustomer(null);
            setCustomerProfile(null);
            setCustomerMessages([]);
            setProfileNewMessage('');
            fetchMessages(); // Refresh messages list
          }}
          uinfo={uinfo}
        />
      )}
    </ReportLayout>
  );
};

export default CustomerContactsReport;
